import { Mail, MessageCircle, MessageSquare, Phone } from 'lucide-react';
import { type ComponentProps, Fragment, type ReactNode } from 'react';

import { ContactAttemptStatusBadge } from '@apps/web/components/outreaches/contact_attempt_status_badge';
import {
    OutreachResponseSummary,
    shouldShowOutreachResponseSummary,
} from '@apps/web/components/outreaches/outreach_response_summary';
import {
    ENTITY_CONFIG,
    ENTITY_PILL_CLASSES,
} from '@apps/web/components/phoebe_chat/entity_visuals';
import { dynamicWidgetBindingProps } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_binding_dom';
import { DynamicWidgetIssueNotice } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_issue_notice';
import { type DynamicWidgetMetricTags } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_metrics';
import {
    type DynamicWidgetIssue,
    type OutreachProgressWidgetModel,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    TEMPLATE_CARD_END_CLASS,
    TEMPLATE_CARD_FOOTER_CLASS,
    TEMPLATE_CARD_HEADER_CLASS,
    TEMPLATE_CARD_ROW_CLASS,
    TEMPLATE_CARD_SUBTITLE_CLASS,
    TEMPLATE_CARD_TITLE_CLASS,
    TEMPLATE_PRIMARY_BUTTON_XS_CLASS,
    TEMPLATE_REPLY_CLASS,
    TEMPLATE_STATUS_CLASS,
    TEMPLATE_TOKEN_CLASS,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/template_theme';
import { type CaregiverAttemptRow } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/use_outreach_progress';
import { Progress } from '@apps/web/components/ui/progress';
import { Spinner } from '@apps/web/components/ui/spinner';
import { StatusBadge } from '@apps/web/components/ui/status_badge';
import { type StatusIntent } from '@apps/web/components/ui/status_tokens';
import { UserAvatar } from '@apps/web/components/ui/user_avatar';
import { formatDurationFromNow } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import {
    type DeliveryMode,
    type DisplayOutreachStatus,
    type OutreachStatus,
} from '@libraries/typescript/api_client';

const DELIVERY_MODE_ICONS: Record<
    DeliveryMode,
    { readonly icon: typeof Phone; readonly label: string }
> = {
    sms: { icon: MessageSquare, label: 'Text message' },
    voice: { icon: Phone, label: 'Phone call' },
    email: { icon: Mail, label: 'Email' },
    chat: { icon: MessageCircle, label: 'Chat message' },
};

const ClientIcon = ENTITY_CONFIG.client.icon;

function outreachStatusDisplay(
    status: DisplayOutreachStatus | OutreachStatus,
    acceptedCount: number,
): { readonly label: string; readonly intent: StatusIntent } {
    switch (status) {
        case 'pending':
            return { label: 'Pending', intent: 'neutral' };
        case 'paused':
            return { label: 'Paused', intent: 'neutral' };
        case 'in_progress':
            return { label: 'In Progress', intent: 'info' };
        case 'awaiting_confirmation':
            return acceptedCount === 1
                ? { label: '1 caregiver available', intent: 'positive' }
                : { label: 'Awaiting confirmation', intent: 'positive' };
        case 'filled':
            return { label: 'Filled', intent: 'positive' };
        case 'unfilled':
            return { label: 'Unfilled', intent: 'neutral' };
        case 'cancelled':
            return { label: 'Archived', intent: 'neutral' };
        case 'shift_missing':
            return { label: 'Shift missing', intent: 'neutral' };
        default: {
            const exhaustiveStatus: never = status;
            return exhaustiveStatus;
        }
    }
}

function CaregiverRowStatus({
    row,
    timezone,
}: {
    readonly row: CaregiverAttemptRow;
    readonly timezone: string;
}) {
    if (row.response === 'available_interested') {
        return (
            <StatusBadge status={{ label: 'Accepted', intent: 'positive' }} />
        );
    }
    if (
        row.response === 'unavailable' ||
        row.response === 'available_not_interested' ||
        row.response === 'not_a_fit'
    ) {
        return (
            <StatusBadge status={{ label: 'Declined', intent: 'negative' }} />
        );
    }
    return (
        <ContactAttemptStatusBadge
            status={row.latestAttempt.outreach_status}
            deliveryMode={row.latestAttempt.delivery_mode}
            communicationDetails={row.latestAttempt.communication_details}
            currentShiftEndTime={row.latestAttempt.current_shift_end}
            showBusyQueuedState={true}
            voiceCallStartedAt={row.latestAttempt.voice_call?.started_at}
            timezone={timezone}
        />
    );
}

function caregiverRowMeta(row: CaregiverAttemptRow, now: Date): ReactNode {
    const parts: Array<{ readonly key: string; readonly node: ReactNode }> = [];
    const distance = row.latestAttempt.distance;
    if (typeof distance === 'number') {
        parts.push({ key: 'distance', node: `${distance.toFixed(1)} mi` });
    }
    parts.push({
        key: 'channels',
        node: (
            <span className="inline-flex items-center gap-1 align-middle">
                {row.attemptsOldestFirst
                    .slice(0, 4)
                    .map((attempt, attemptIndex) => {
                        const channel =
                            DELIVERY_MODE_ICONS[attempt.delivery_mode];
                        const ChannelIcon = channel.icon;
                        return (
                            <ChannelIcon
                                key={`${attempt.id}-${attemptIndex}`}
                                className="size-3"
                                aria-label={channel.label}
                            />
                        );
                    })}
            </span>
        ),
    });
    const repliedAt = row.attemptsOldestFirst
        .map((attempt) => attempt.response_timestamp)
        .filter((value): value is string => typeof value === 'string')
        .pop();
    if (repliedAt !== undefined) {
        parts.push({
            key: 'replied',
            node: `replied ${formatDurationFromNow(repliedAt, now)} ago`,
        });
    }
    return parts.map((part, partIndex) => (
        <Fragment key={part.key}>
            {partIndex > 0 ? ' · ' : null}
            {part.node}
        </Fragment>
    ));
}

function caregiverVoiceTranscript(row: CaregiverAttemptRow): string | null {
    for (const attempt of [...row.attemptsOldestFirst].reverse()) {
        if (
            attempt.delivery_mode === 'voice' &&
            typeof attempt.transcript_text === 'string' &&
            attempt.transcript_text.trim().length > 0
        ) {
            return attempt.transcript_text;
        }
    }
    return null;
}

export function OutreachProgressHeader({
    model,
    clientNames,
    status,
    acceptedCount,
    isAgentAnalyzing,
    isLoadingOutreach,
    responseCounts,
    rowCount,
    queuedCaregiverCount,
    outreachStatus,
    assignedCaregiverNames,
    cancellationReason,
    creationFacts,
    contentIssues,
    metricTags,
    acceptPolicyLabel = null,
}: {
    readonly model: OutreachProgressWidgetModel;
    readonly clientNames: ReadonlyArray<string>;
    readonly status: DisplayOutreachStatus | OutreachStatus | null;
    readonly acceptedCount: number;
    readonly isAgentAnalyzing: boolean;
    readonly isLoadingOutreach: boolean;
    readonly responseCounts: ComponentProps<
        typeof OutreachResponseSummary
    >['responseCounts'];
    readonly rowCount: number;
    readonly queuedCaregiverCount: number;
    readonly outreachStatus: OutreachStatus | undefined;
    readonly assignedCaregiverNames: ReadonlyArray<string>;
    readonly cancellationReason: string | null | undefined;
    readonly creationFacts: ReadonlyArray<string>;
    readonly contentIssues: ReadonlyArray<DynamicWidgetIssue>;
    readonly metricTags: DynamicWidgetMetricTags;
    readonly acceptPolicyLabel?: string | null;
}) {
    const summaryParts: Array<{
        readonly key: string;
        readonly node: ReactNode;
    }> = [];
    if (isAgentAnalyzing) {
        summaryParts.push({
            key: 'analyzing',
            node: 'Phoebe is still matching caregivers',
        });
    }
    if (
        shouldShowOutreachResponseSummary({
            displayStatus: status,
            isAgentAnalyzing,
        })
    ) {
        summaryParts.push({
            key: 'responses',
            node: (
                <OutreachResponseSummary
                    responseCounts={responseCounts}
                    displayStatus={status}
                    isAgentAnalyzing={isAgentAnalyzing}
                />
            ),
        });
    }
    if (rowCount === 0 && queuedCaregiverCount > 0) {
        summaryParts.push({
            key: 'queued',
            node: `${queuedCaregiverCount} caregiver${
                queuedCaregiverCount === 1 ? '' : 's'
            } queued`,
        });
    }
    if (outreachStatus === 'filled' && assignedCaregiverNames.length > 0) {
        summaryParts.push({
            key: 'filled-by',
            node: `Filled by ${assignedCaregiverNames.join(', ')}`,
        });
    }
    if (outreachStatus === 'cancelled') {
        summaryParts.push({
            key: 'cancelled',
            node: cancellationReason
                ? `Cancelled — ${cancellationReason}`
                : 'Cancelled',
        });
    }
    const statusDisplay =
        status === null ? null : outreachStatusDisplay(status, acceptedCount);

    return (
        <div className={TEMPLATE_CARD_HEADER_CLASS}>
            <div className="min-w-0 flex-1">
                <p
                    className={cn(
                        TEMPLATE_CARD_TITLE_CLASS,
                        'flex flex-wrap items-center gap-1.5',
                    )}
                >
                    Outreach for
                    {clientNames.length === 1 && clientNames[0] ? (
                        <span
                            className={cn(
                                'inline-flex max-w-full min-w-0 items-center gap-0.5 rounded px-1 align-middle text-[13px] font-medium whitespace-nowrap',
                                ENTITY_PILL_CLASSES.client,
                            )}
                        >
                            <ClientIcon
                                className="size-3.5 shrink-0"
                                aria-hidden="true"
                            />
                            <span
                                className="min-w-0 truncate"
                                {...dynamicWidgetBindingProps(
                                    model.widgetId,
                                    'live.clients',
                                )}
                            >
                                {clientNames[0]}
                            </span>
                        </span>
                    ) : null}
                    {clientNames.length > 1 ? (
                        <span className="font-normal text-[#617578]">
                            {clientNames.length} clients
                        </span>
                    ) : null}
                </p>
                {summaryParts.length > 0 ? (
                    <p className={TEMPLATE_CARD_SUBTITLE_CLASS}>
                        {summaryParts.map((part, partIndex) => (
                            <Fragment key={part.key}>
                                {partIndex > 0 ? ' · ' : null}
                                {part.node}
                            </Fragment>
                        ))}
                    </p>
                ) : null}
                {creationFacts.length > 0 ? (
                    <p className="text-[11px] text-[#829494]">
                        Created with {creationFacts.join(' · ')}
                    </p>
                ) : null}
                {contentIssues.map((issue) => (
                    <DynamicWidgetIssueNotice
                        key={issue.id}
                        issue={issue}
                        metricTags={metricTags}
                    />
                ))}
            </div>
            <div className={TEMPLATE_CARD_END_CLASS}>
                {acceptPolicyLabel !== null ? (
                    <span className={TEMPLATE_TOKEN_CLASS}>
                        {acceptPolicyLabel}
                    </span>
                ) : null}
                {statusDisplay === null && isLoadingOutreach ? (
                    <Spinner size="xs" />
                ) : null}
                {statusDisplay !== null ? (
                    <StatusBadge
                        status={statusDisplay}
                        className={TEMPLATE_STATUS_CLASS[statusDisplay.intent]}
                        {...dynamicWidgetBindingProps(
                            model.widgetId,
                            'live.status',
                        )}
                    />
                ) : null}
            </div>
        </div>
    );
}

export function OutreachProgressRail({
    model,
    done,
    total,
}: {
    readonly model: OutreachProgressWidgetModel;
    readonly done: number;
    readonly total: number;
}) {
    if (total === 0) {
        return null;
    }
    return (
        <div
            className="border-b border-[#E9F0F0] px-3.5 pt-2.5 pb-3"
            {...dynamicWidgetBindingProps(model.widgetId, 'live.progress')}
        >
            <Progress
                value={(done / total) * 100}
                className="h-1.5 bg-[rgba(85,51,238,0.15)]"
                indicatorClassName="bg-[#5533EE] transition-[width] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
                aria-label={`Contacted ${done} of ${total} caregivers`}
            />
        </div>
    );
}

export function OutreachCaregiverRow({
    model,
    row,
    timezone,
    now,
    onCaregiverClick,
    onAssignRequest,
}: {
    readonly model: OutreachProgressWidgetModel;
    readonly row: CaregiverAttemptRow;
    readonly timezone: string;
    readonly now: Date;
    readonly onCaregiverClick?: (caregiverId: string) => void;
    readonly onAssignRequest?: (row: CaregiverAttemptRow) => void;
}) {
    const transcript = caregiverVoiceTranscript(row);
    const canAssign =
        row.response === 'available_interested' &&
        onAssignRequest !== undefined;
    return (
        <div
            className={TEMPLATE_CARD_ROW_CLASS}
            {...dynamicWidgetBindingProps(model.widgetId, 'live.attemptRows')}
        >
            <UserAvatar
                displayName={row.displayName}
                colorKey={row.caregiverId}
                className="size-7"
            />
            <div className="min-w-0 flex-1">
                <div className="truncate font-medium">
                    {onCaregiverClick ? (
                        <button
                            type="button"
                            className="hover:text-foreground cursor-pointer underline-offset-2 hover:underline"
                            onClick={() => onCaregiverClick(row.caregiverId)}
                        >
                            {row.displayName}
                        </button>
                    ) : (
                        row.displayName
                    )}
                </div>
                <p className="truncate text-xs text-[#617578]">
                    {caregiverRowMeta(row, now)}
                </p>
            </div>
            <div className={TEMPLATE_CARD_END_CLASS}>
                <CaregiverRowStatus row={row} timezone={timezone} />
                {canAssign ? (
                    <button
                        type="button"
                        className={TEMPLATE_PRIMARY_BUTTON_XS_CLASS}
                        onClick={() => onAssignRequest(row)}
                    >
                        Assign
                    </button>
                ) : null}
            </div>
            {transcript !== null ? (
                <div className="mt-1.5 ml-[38px] flex basis-full">
                    <div className={TEMPLATE_REPLY_CLASS}>
                        <span className="block text-[11px] text-[#617578]">
                            Call transcript
                        </span>
                        <span className="line-clamp-3 block">{transcript}</span>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export function OutreachReadErrorFooter({
    footerNote,
    readIssue,
    metricTags,
    onRetryRead,
}: {
    readonly footerNote: string;
    readonly readIssue: DynamicWidgetIssue | null;
    readonly metricTags: DynamicWidgetMetricTags;
    readonly onRetryRead: () => void;
}) {
    if (footerNote.length === 0 && readIssue === null) {
        return null;
    }
    return (
        <div className={TEMPLATE_CARD_FOOTER_CLASS}>
            {footerNote.length > 0 ? (
                <p className="text-muted-foreground min-w-0 text-xs">
                    {footerNote}
                </p>
            ) : null}
            {readIssue !== null ? (
                <div className="min-w-0 flex-1">
                    <DynamicWidgetIssueNotice
                        issue={readIssue}
                        metricTags={metricTags}
                        onRetryRead={onRetryRead}
                    />
                </div>
            ) : null}
        </div>
    );
}
